import { EventName, mixTrack } from '@services/mixpanel';
import { LegacyWorkoutFormValues } from '../../workout/models/workoutEdit.model';
import {
  AutoplayTemplates_TabataAndHiitEvent,
  RunningTemplateSelectedEvent,
} from '@services/mixpanel/events';
import { isRunningExercise } from '../../workout/view-models/mappers';
import {
  Circuit,
  Running,
  WorkoutItem,
  RunningExerciseType,
} from '../../workout/models/workout.domain';
import { workout } from '@client/new/common/onboardingFlow/flowEducationalModal.css';

type WorkoutType = 'Tabata' | 'HIIT' | 'Both' | 'Running';

interface TrackWorkoutParams<T extends WorkoutType> {
  workout: LegacyWorkoutFormValues;
  eventName: TrackableEventByType<T>;
  workoutId: number;
}

type TabataHiitEvents = Extract<
  EventName,
  | 'Autoplay Template Published'
  | 'Autoplay Template Saved'
  | 'Autoplay Template Scheduled'
>;

type RunningEvents = Extract<
  EventName,
  | 'Running Template Published'
  | 'Running Template Saved'
  | 'Running Template Scheduled'
>;

type TrackableEventByType<T extends WorkoutType> = T extends 'Running'
  ? RunningEvents
  : TabataHiitEvents;

export const detectWorkoutType = (
  workout: LegacyWorkoutFormValues,
): WorkoutType | null => {
  if (!workout.exercises?.length) return null;

  const hasTabata = workout.exercises.some(
    (ex: WorkoutItem) => ex?.type === 10,
  );
  const hasHIIT = workout.exercises.some((ex: WorkoutItem) => ex?.type === 11);
  const hasRunning = workout.exercises.some(
    (ex: WorkoutItem) => ex.type === 100,
  );

  if (hasRunning) return 'Running';
  if (hasTabata && hasHIIT) return 'Both';
  if (hasTabata) return 'Tabata';
  if (hasHIIT) return 'HIIT';
  return null;
};

export const getRunningExerciseType = (
  exercises: WorkoutItem[],
): RunningExerciseType | null => {
  if (!exercises?.length) return null;

  for (const exercise of exercises) {
    if (!exercise || !exercise.type) continue;

    if (exercise.type === 100) {
      const runningExercise = exercise as Running<RunningExerciseType>;
      if (runningExercise.variant) {
        return runningExercise.variant;
      }
    }

    if (
      exercise.type === 3 &&
      exercise.uuid &&
      isRunningExercise(exercise as Circuit)
    ) {
      const runningExercise = exercise as Running<RunningExerciseType>;
      if (runningExercise.variant) {
        return runningExercise.variant;
      }
    }
  }

  return null;
};

const track = <T extends WorkoutType>({
  workout,
  eventName,
  workoutId,
}: TrackWorkoutParams<T>) => {
  const type = detectWorkoutType(workout);

  if (!type) return;

  if (type === 'Running') {
    mixTrack({
      type: eventName,
      payload: { type: getRunningExerciseType(workout.exercises), workoutId },
    } as RunningTemplateSelectedEvent);
  } else {
    mixTrack({
      type: eventName,
      payload: { type, workoutId },
    } as AutoplayTemplates_TabataAndHiitEvent);
  }
};

const getEventNames = (workoutType: WorkoutType) => {
  if (workoutType === 'Running') {
    return {
      saved: 'Running Template Saved' as const,
      scheduled: 'Running Template Scheduled' as const,
      published: 'Running Template Published' as const,
    };
  }

  return {
    saved: 'Autoplay Template Saved' as const,
    scheduled: 'Autoplay Template Scheduled' as const,
    published: 'Autoplay Template Published' as const,
  };
};

export const trackWorkoutEvent = (
  workout: LegacyWorkoutFormValues,
  workoutId: number,
) => {
  const workoutType = detectWorkoutType(workout);
  if (!workoutType) return;

  const eventNames = getEventNames(workoutType);

  if (!workout.publishedAt) {
    return track({
      workout,
      eventName: eventNames.saved,
      workoutId,
    });
  }

  const published = new Date(workout.publishedAt);
  const now = new Date();
  const diffInMinutes = (now.getTime() - published.getTime()) / (1000 * 60);

  if (diffInMinutes < 10) {
    return track({
      workout,
      eventName: eventNames.scheduled,
      workoutId,
    });
  }

  return track({
    workout,
    eventName: eventNames.published,
    workoutId,
  });
};
